/**
 * A component that renders a single group's view of a planning market
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Link, useMediaQuery, useTheme } from '@material-ui/core';
import Screen from '../../../containers/Screen/Screen';
import { firstDisplayedCriticalBug, firstDisplayedOpenDiscussion, openCriticalBug, useOpenChipNotification } from '../../../utils/openChipNotification';
import { useSearchMatchNavigation } from '../../../utils/searchMatchNavigation';
import {
  ISSUE_TYPE,
  QUESTION_TYPE,
  REPLY_TYPE,
  REPORT_TYPE,
  SUGGEST_CHANGE_TYPE,
  TODO_TYPE
} from '../../../constants/comments';
import { getSortedRoots } from '../../../containers/CommentBox/CommentBox';
import { MarketPresencesContext } from '../../../contexts/MarketPresencesContext/MarketPresencesContext';
import {
  getGroupPresences, getHumanPresences,
  getMarketPresences,
  getPresenceMap, isAutonomousGroup
} from '../../../contexts/MarketPresencesContext/marketPresencesHelper';
import DismissableText from '../../../components/Notifications/DismissableText';
import ArchiveInvestbiles from '../../DialogArchives/ArchiveInvestibles';
import { getInvestible, getInvestiblesInStage } from '../../../contexts/InvestibesContext/investiblesContextHelper';
import { getMarketInfo } from '../../../utils/userFunctions';
import MarketTodos from './MarketTodos';
import {
  getFullStage, getFurtherWorkStage,
  isAcceptedStage,
  isBlockedStage,
  isFurtherWorkStage,
  isInReviewStage,
  isNotDoingStage,
  isRequiredInputStage
} from '../../../contexts/MarketStagesContext/marketStagesContextHelper';
import { SearchResultsContext } from '../../../contexts/SearchResultsContext/SearchResultsContext';
import { getPageReducerPage, usePageStateReducer } from '../../../components/PageState/pageStateHooks';
import AssignmentOutlined from '@material-ui/icons/AssignmentOutlined';
import { MarketGroupsContext } from '../../../contexts/MarketGroupsContext/MarketGroupsContext';
import { getGroup } from '../../../contexts/MarketGroupsContext/marketGroupsContextHelper';
import { GmailTabItem, GmailTabs } from '../../../containers/Tab/Inbox';
import { AssignmentIndOutlined, BugReportOutlined } from '@material-ui/icons';
import Backlog, { BacklogItem } from './Backlog';
import { PAGE_SIZE } from '../../../components/Comments/BugListContext';
import { getPaginatedItems } from '../../../utils/messageUtils';
import { Box, IconButton } from '@material-ui/core';
import { ExpandLess, KeyboardArrowLeft } from '@material-ui/icons';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import DiscussionSection from './DiscussionSection';
import InvestiblesByPerson from './InvestiblesByPerson';
import { NO_SECTION_TYPE } from '../../../constants/global';
import SubSection from '../../../containers/SubSection/SubSection';
import {
  addMarketComments,
  filterToRoot,
  getInvestibleComments,
  getMarketComments
} from '../../../contexts/CommentsContext/commentsContextHelper';
import {
  ASSIGNED_HASH,
  BACKLOG_HASH,
  DISCUSSION_HASH,
  formGroupEditLink, formGroupManageLink, formInvestibleLink,
  formMarketAddInvestibleLink, formWizardLink,
  navigate,
  removeHash
} from '../../../utils/marketIdPathFunctions';
import { DISCUSSION_WIZARD_TYPE, JOB_STAGE_WIZARD_TYPE, JOB_WIZARD_TYPE,
  SUPPORT_SUB_TYPE } from '../../../constants/markets';
import InlineWizardHost from '../../../components/InlineWizard/InlineWizardHost';
import { InlineWizardLaunchContext } from '../../../components/InlineWizard/InlineWizardContext';
import DialogOutset from './DialogOutset';
import SettingsIcon from '@material-ui/icons/Settings';
import SpinningButton from '../../../components/SpinBlocking/SpinningButton';
import TooltipIconButton from '../../../components/Buttons/TooltipIconButton';
import { useButtonColors } from '../../../components/Buttons/ButtonConstants';
import { wizardStyles } from '../../../components/AddNewWizards/WizardStylesContext';
import AddIcon from '@material-ui/icons/Add';
import { stageChangeInvestible } from '../../../api/investibles';
import { onInvestibleStageChange } from '../../../utils/investibleFunctions';
import { InvestiblesContext } from '../../../contexts/InvestibesContext/InvestiblesContext';
import { MarketStagesContext } from '../../../contexts/MarketStagesContext/MarketStagesContext';
import { CommentsContext } from '../../../contexts/CommentsContext/CommentsContext';
import { OperationInProgressContext } from '../../../contexts/OperationInProgressContext/OperationInProgressContext';
import { MarketsContext } from '../../../contexts/MarketsContext/MarketsContext';
import { getMarket } from '../../../contexts/MarketsContext/marketsContextHelper';
import { useHotkeys } from 'react-hotkeys-hook';
import { findMessagesForCommentIds, findMessagesForInvestibleIds } from '../../../utils/messageUtils';
import { useSyncedMessages } from '../../../contexts/SyncedMessagesContext/SyncedMessagesContext';
import { NotificationsContext } from '../../../contexts/NotificationsContext/NotificationsContext';
import { isInInbox } from '../../../contexts/NotificationsContext/notificationsContextHelper';
import { RED_LEVEL } from '../../../constants/notifications';
import { GroupMembersContext } from '../../../contexts/GroupMembersContext/GroupMembersContext';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { DiffContext } from '../../../contexts/DiffContext/DiffContext';
import { calculateInvestibleVoters } from '../../../utils/votingUtils';
import { getSwimlaneInvestiblesForStage } from './userUtils';
import LightbulbOutlined from '../../../components/CustomChip/LightbulbOutlined';
import useDoableStageGuard from '../../../components/AddNewWizards/JobStage/useDoableStageGuard';

function getAnchorId(tabIndex) {
  switch (tabIndex) {
    case 0:
      return 'storiesSection';
    case 1:
      return 'backlogSection';
    case 2:
      return 'marketTodos'
    case 3:
      return 'discussionSection';
    default:
      return 'storiesSection';
  }
}

function PlanningDialog(props) {
  const {
    marketInvestibles = [],
    marketStages = [],
    hidden = false,
    myPresence,
    banner,
    marketId,
    groupId
  } = props;
  const confirmDoableQuestions = useDoableStageGuard(marketId);
  const [searchResults] = useContext(SearchResultsContext);
  const { results, parentResults, search } = searchResults;
  const history = useHistory();
  const openChipNotification = useOpenChipNotification();
  const { firstCommentMatch, openMatch } = useSearchMatchNavigation();
  const [messagesState] = useContext(NotificationsContext);
  const location = useLocation();
  const { hash } = location;
  const intl = useIntl();
  const wizardClasses = wizardStyles();
  const theme = useTheme();
  const { actionButtonColor } = useButtonColors();
  const refToTop = useRef();
  const mobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  // C-all-1001: same treatment as the job header (C-all-994 / Q-all-138 O-4) -
  // text-only view tabs on desktop, outlined icons only on mobile (where the
  // GmailTabItem label is hidden for space at the sm breakpoint).
  const iconOnlyTabs = useMediaQuery(theme.breakpoints.down('sm'));
  const [investibleState, investiblesDispatch] = useContext(InvestiblesContext);
  const [marketStagesState] = useContext(MarketStagesContext);
  const [commentsState, commentsDispatch] = useContext(CommentsContext);
  const [, marketPresencesDispatch] = useContext(MarketPresencesContext);
  const [operationRunning, setOperationRunning] = useContext(OperationInProgressContext);
  const [groupState] = useContext(MarketGroupsContext);
  const [marketsState] = useContext(MarketsContext);
  // J-all-440: view tab counts read the synced set only.
  const syncedMessages = useSyncedMessages();
  const [groupPresencesState] = useContext(GroupMembersContext);
  const [, diffDispatch] = useContext(DiffContext);
  const market = getMarket(marketsState, marketId) || {};
  const isSupport = market?.market_sub_type === SUPPORT_SUB_TYPE;
  const group = getGroup(groupState, marketId, groupId);
  const { name: groupName } = group || {};
  const marketComments = getMarketComments(commentsState, marketId) || [];
  const unResolvedGroupComments = marketComments.filter(comment => !comment.investible_id &&
    !comment.resolved && comment.group_id === groupId) || [];
  const resolvedTodoRoots = marketComments.filter(comment => !comment.investible_id &&
    comment.resolved && comment.group_id === groupId && comment.comment_type === TODO_TYPE) || [];
  const resolvedTodoRootIds = new Set(resolvedTodoRoots.map((c) => c.id));
  const resolvedTodoReplies = marketComments.filter(comment => comment.comment_type === REPLY_TYPE &&
    resolvedTodoRootIds.has(comment.root_comment_id));
  const resolvedTodoGroupComments = resolvedTodoRoots.concat(resolvedTodoReplies);
  // There is no link to a reply so including them should be okay
  const notTodoGroupComments = unResolvedGroupComments.filter(comment =>
    [QUESTION_TYPE, SUGGEST_CHANGE_TYPE, REPORT_TYPE, REPLY_TYPE].includes(comment.comment_type)) || [];
  const resolvedDiscussionComments = marketComments.filter(comment =>
    comment.group_id === groupId && !comment.investible_id && comment.resolved &&
    [QUESTION_TYPE, SUGGEST_CHANGE_TYPE, REPORT_TYPE].includes(comment.comment_type)) || [];
  const [marketPresencesState] = useContext(MarketPresencesContext);
  const [jobSearchPage, setJobSearchPage] = useState(1);
  const [pageStateFull, pageDispatch] = usePageStateReducer('group');
  const [pageState, updatePageState] = getPageReducerPage(pageStateFull, pageDispatch, groupId,
    {sectionOpen: 'storiesSection', tabIndex: 0 });
  const {
    sectionOpen,
    tabIndex,
    nextArchiveCollapsed
  } = pageState;
  // J-all-325 (T-all-2186): add wizards open inside this view instead of the full-screen /wizard route.
  // Kept local (not in the URL) so navigating away and back shows the view normally (T-all-2188); any
  // navigation clears it via the effect below.
  const [inlineWizard, setInlineWizard] = useState(undefined);
  function openInlineWizard(descriptor) {
    setInlineWizard(descriptor);
  }
  function closeInlineWizard() {
    setInlineWizard(undefined);
  }
  useEffect(() => {
    setInlineWizard(undefined);
  }, [location.pathname, location.hash]);
  const marketPresences = getMarketPresences(marketPresencesState, marketId) || [];
  const groupPresences = getGroupPresences(marketPresences, groupPresencesState, marketId, groupId) || [];
  const humanGroupPresences = getHumanPresences(groupPresences);
  const isSingleUser = humanGroupPresences.length === 1;
  const isAutonomous = isAutonomousGroup(groupPresences, group);
  const furtherWorkStage = marketStages.find((stage) => isFurtherWorkStage(stage)) || {};
  const investiblesFullAssist = marketInvestibles.filter((investible) => {
    const marketInfo = getMarketInfo(investible, marketId);
    const { group_id: otherGroupId, investible_id: investibleId } = marketInfo;
    const otherGroupPresences = getGroupPresences(marketPresences, groupPresencesState, marketId, otherGroupId) || [];
    const thisGroupPresenceId = isAutonomous ? groupPresences[0].id : undefined;
    const isGroupMember = !_.isEmpty(otherGroupPresences.find((aPresence) =>
      aPresence.id === thisGroupPresenceId));
    const investedOrAddressed = calculateInvestibleVoters(investibleId, marketId, marketsState, investibleState,
      marketPresences, true, true);
    const isSubscribed = !_.isEmpty(investedOrAddressed.find((investor) => !investor.abstain &&
      investor.id === thisGroupPresenceId));
    return otherGroupId === groupId || (isAutonomous && marketInfo.assigned?.includes(thisGroupPresenceId))
      || (isAutonomous && marketInfo.stage === furtherWorkStage.id && (isGroupMember || isSubscribed));
  });
  const investiblesViewOnly = marketInvestibles.filter((investible) => {
    const marketInfo = getMarketInfo(investible, marketId);
    return marketInfo.group_id === groupId;
  });
  const acceptedStage = marketStages.find(stage => isAcceptedStage(stage)) || {};
  const inDialogStage = marketStages.find(stage => stage.allows_investment) || {};
  const notDoingStage = marketStages.find(stage => isNotDoingStage(stage)) || {};
  const inReviewStage = marketStages.find(stage => isInReviewStage(stage)) || {};
  const inBlockingStage = marketStages.find(stage => isBlockedStage(stage)) || {};
  const visibleStages = marketStages.filter((stage) => stage.appears_in_context) || [];
  const requiresInputStage = marketStages.find((stage) => isRequiredInputStage(stage)) || {};
  const furtherWorkInvestiblesFullAssist = getInvestiblesInStage(investiblesFullAssist, furtherWorkStage.id,
    marketId) || [];
  const furtherWorkReadyToStartFullAssist = _.remove(furtherWorkInvestiblesFullAssist, (investible) => {
    const marketInfo = getMarketInfo(investible, marketId);
    const { open_for_investment: openForInvestment } = marketInfo;
    return openForInvestment;
  });
  const furtherWorkNotReadyFullAssist = furtherWorkInvestiblesFullAssist.filter((investible) => {
    const investibleComments = getInvestibleComments(investible.investible.id, marketId, commentsState);
    return !_.isEmpty(investibleComments.find((comment) => !comment.resolved &&
      [QUESTION_TYPE, SUGGEST_CHANGE_TYPE, ISSUE_TYPE].includes(comment.comment_type)));
  });
  const requiresInputInvestiblesFullAssist = getInvestiblesInStage(investiblesFullAssist, requiresInputStage.id,
    marketId);
  const unassignedRequiresInputInvestibles = (requiresInputInvestiblesFullAssist || []).filter((investible) => {
    const marketInfo = getMarketInfo(investible, marketId) || {};
    return _.isEmpty(marketInfo.assigned);
  });
  const blockedInvestiblesFullAssist = getInvestiblesInStage(investiblesFullAssist, inBlockingStage.id, marketId);
  const blockedOrRequiresInputOrReadyInvestiblesFullAssist = blockedInvestiblesFullAssist
    .concat(requiresInputInvestiblesFullAssist).concat(furtherWorkReadyToStartFullAssist)
    .concat(furtherWorkNotReadyFullAssist);
  const archiveInvestiblesFullAssist = blockedInvestiblesFullAssist
    .concat(unassignedRequiresInputInvestibles).concat(furtherWorkReadyToStartFullAssist)
    .concat(furtherWorkNotReadyFullAssist);
  const swimlaneInvestibles = investiblesFullAssist.filter((inv) => {
    const marketInfo = getMarketInfo(inv, marketId) || {};
    const stage = marketStages.find((stage) => stage.id === marketInfo.stage);
    return stage && stage.appears_in_context && stage.allows_tasks;
  });
  const swimlaneCompleteInvestibles = getSwimlaneInvestiblesForStage(investiblesFullAssist, inReviewStage,
    marketId, marketComments, marketStagesState);
  // During search use all complete investibles (uncapped) per Q-all-65 O-3
  const allCompleteInvestibles = !_.isEmpty(search)
    ? getInvestiblesInStage(investiblesFullAssist, inReviewStage.id, marketId)
    : swimlaneCompleteInvestibles;
  const jobProgressSearchList = !_.isEmpty(search)
    // Order by the date each row shows (investible.created_at) so most recent is on top (T-all-2236).
    ? _.orderBy(blockedOrRequiresInputOrReadyInvestiblesFullAssist
        .concat(swimlaneInvestibles)
        .concat(allCompleteInvestibles), [(inv) => inv.investible.created_at], ['desc'])
    : [];
  const activeInvestibles = swimlaneInvestibles.filter((inv) => {
    const marketInfo = getMarketInfo(inv, marketId) || {};
    return marketInfo.assigned?.includes(myPresence.id);
  });
  const presenceMap = getPresenceMap(marketPresences);
  const {
    first: jobSearchFirst,
    last: jobSearchLast,
    data: jobSearchData,
    hasMore: jobSearchHasMore,
    hasLess: jobSearchHasLess
  } = getPaginatedItems(jobProgressSearchList, jobSearchPage, PAGE_SIZE);
  const myGroupPresence = groupPresences.find((presence) => presence.id === myPresence.id);

  function isSectionOpen(section) {
    return sectionOpen === section || (!sectionOpen && section === 'storiesSection');
  }

  useHotkeys('ctrl+a', () => openInlineWizard({ wizardType: JOB_WIZARD_TYPE, marketId, groupId }),
    {enabled: !hidden && !inlineWizard && (isSectionOpen('backlogSection')||isSectionOpen('storiesSection'))},
    [groupId, marketId, hidden, inlineWizard]);
  useHotkeys('ctrl+q', () => openInlineWizard({ wizardType: DISCUSSION_WIZARD_TYPE, marketId, groupId,
      commentType: QUESTION_TYPE }),
    {enabled: !hidden && !inlineWizard}, [groupId, marketId, hidden, inlineWizard]);
  useHotkeys('ctrl+alt+s', () => openInlineWizard({ wizardType: DISCUSSION_WIZARD_TYPE, marketId, groupId,
      commentType: SUGGEST_CHANGE_TYPE }),
    {enabled: !hidden && !inlineWizard}, [groupId, marketId, hidden, inlineWizard]);
  useHotkeys('ctrl+alt+n', () => openInlineWizard({ wizardType: DISCUSSION_WIZARD_TYPE, marketId, groupId,
      commentType: REPORT_TYPE }),
    {enabled: !hidden && !inlineWizard}, [groupId, marketId, hidden, inlineWizard]);

  const targetContext = location.state?.searchMatch || location.state?.notification;
  useEffect(() => {
    if (hash && !hidden) { 
      if (hash.startsWith('#option') || hash === `#${DISCUSSION_HASH}`) {
        if (sectionOpen !== 'discussionSection') {
          updatePageState({ sectionOpen: 'discussionSection', tabIndex: 3 });
        }
        if (hash === `#${DISCUSSION_HASH}`) {
          removeHash(history);
        }
      } else if (hash.includes(ASSIGNED_HASH)) {
        if (sectionOpen !== 'storiesSection') {
          updatePageState({ sectionOpen: 'storiesSection', tabIndex: 0 });
        }
        // Not looking to scroll to this so scroll context won't touch it
        removeHash(history);
      } else if (hash.includes(BACKLOG_HASH)) {
        if (sectionOpen !== 'backlogSection') {
          updatePageState({ sectionOpen: 'backlogSection', tabIndex: 1 });
        }
        removeHash(history);
      } else {
        const element = document.getElementById(hash.substring(1, hash.length));
        if (!element) {
          const comments = getMarketComments(commentsState, marketId) || [];
          const found = comments.find((comment) => hash.includes(comment.id) ||
            (comment.inline_market_id && comment.inline_market_id === targetContext?.marketId));
          if (!_.isEmpty(found)) {
            const rootComment = filterToRoot(comments, found.id);
            if (_.isEmpty(rootComment.investible_id)) {
              if (rootComment.comment_type !== TODO_TYPE) {
                // TO DO TYPE, resolved or not, handled by MarketTodos so is no op here
                // DiscussionSection switches to its resolved tab when the comment is resolved
                if (sectionOpen !== 'discussionSection') {
                  updatePageState({ sectionOpen: 'discussionSection', tabIndex: 3 });
                }
              }
            }
          }
        }
      }
    }
  }, [commentsState, groupId, hash, hidden, history, marketId, updatePageState, sectionOpen, targetContext?.marketId]);

  function openSubSection(subSection) {
    updatePageState({sectionOpen: subSection});
  }

  const sortedGroupRoots = getSortedRoots(notTodoGroupComments, searchResults);
  const questionSuggestionGroupComments = sortedGroupRoots.filter((comment) =>
    [QUESTION_TYPE, SUGGEST_CHANGE_TYPE].includes(comment.comment_type));
  const todoGroupComments = unResolvedGroupComments.filter((comment) => {
    if (_.isEmpty(search)) {
      return comment.comment_type === TODO_TYPE;
    }
    return comment.comment_type === TODO_TYPE && (results.find((item) => item.id === comment.id)
      || parentResults.find((id) => id === comment.id));
  });
  const criticalTodoGroupComments = todoGroupComments.filter((comment) =>
    comment.notification_type === RED_LEVEL);
  const jobsSearchResults = _.size(jobProgressSearchList);
  const furtherWorkInvestibles = getInvestiblesInStage(investiblesViewOnly, furtherWorkStage.id,
    marketId) || [];
  const furtherWorkReadyToStart = _.remove(furtherWorkInvestibles, (investible) => {
    const marketInfo = getMarketInfo(investible, marketId);
    const { open_for_investment: openForInvestment } = marketInfo;
    return openForInvestment;
  });
  const notDoingInvestibles = getInvestiblesInStage(investiblesViewOnly, notDoingStage.id,
    marketId) || [];
  const backlogSearchResults = _.size(furtherWorkReadyToStart) + _.size(furtherWorkInvestibles)
    + _.size(notDoingInvestibles);
  let navListItemTextArray = undefined;
  if (mobileLayout) {
    navListItemTextArray = [
      {icon: SettingsIcon, text: intl.formatMessage({id: 'settings'}),
        target: formGroupEditLink(marketId, groupId), num: 0, isBold: false},
      {icon: PersonAddIcon, text: intl.formatMessage({id: 'manageMembers'}),
        target: formGroupManageLink(marketId, groupId), num: 0, isBold: false}
    ];
  }

  function resetFunction(tabIndex) {
    // J-all-325: switching tabs leaves the wizard's context, so close it.
    closeInlineWizard();
    updatePageState({tabIndex});
    const anchorId = getAnchorId(tabIndex);
    openSubSection(anchorId);
    refToTop.current?.scrollIntoView({ block: "end" });
  }

  function onDropBug(id, isAssigned) {
    let bugBaseUrl = `${formMarketAddInvestibleLink(marketId, groupId)}&fromCommentId=${id}&isAssigned=${isAssigned}`;
    if (!isAssigned) {
      bugBaseUrl += "&jobType=0";
    }
    navigate(history, bugBaseUrl);
  }

  function changeInvestibleReady(id, stage, openForInvestment, resolveCommentIds) {
    const moveInfo = {
      marketId,
      investibleId: id,
      stageInfo: {
        current_stage_id: stage,
        stage_id: furtherWorkStage.id,
        open_for_investment: openForInvestment
      },
    };
    if (resolveCommentIds) {
      moveInfo.stageInfo.resolve_comment_ids = resolveCommentIds;
    }
    setOperationRunning(true);
    return stageChangeInvestible(moveInfo)
      .then((response) => {
        let newInv;
        if (resolveCommentIds) {
          const {full_investible: fullInvestible, comments: resolvedComments } = response;
          newInv = fullInvestible;
          addMarketComments(commentsDispatch, marketId, resolvedComments);
        } else {
          newInv = response;
        }
        onInvestibleStageChange(furtherWorkStage.id, newInv, id, marketId, commentsState,
          commentsDispatch, investiblesDispatch, () => {}, marketStagesState, undefined,
          getFullStage(marketStagesState, marketId, stage), marketPresencesDispatch);
        setOperationRunning(false);
      });
  }

  function getAssistCommentIds(investibleId) {
    const allAssistComments = marketComments.filter((comment) => comment.investible_id === investibleId
      && !comment.resolved && [ISSUE_TYPE, QUESTION_TYPE, SUGGEST_CHANGE_TYPE].includes(comment.comment_type));
    if (_.isEmpty(allAssistComments)) {
      return undefined;
    }
    return allAssistComments.map((comment) => comment.id);
  }

  function onDropJob(id, isAssigned) {
    if (isAssigned) {
      if (isSingleUser) {
        // If autonomous and you are not in the group then this is a no op
        const inv = getInvestible(investibleState, id);
        const marketInfo = getMarketInfo(inv, marketId) || {};
        if (marketInfo.stage !== acceptedStage.id) {
          const fullMoveStage = !_.isEmpty(myGroupPresence) ? acceptedStage : inDialogStage;
          if (confirmDoableQuestions(id, fullMoveStage.id, myGroupPresence.id)) {
            return Promise.resolve(false);
          }
          const moveInfo = {
            marketId,
            investibleId: id,
            stageInfo: {
              current_stage_id: marketInfo.stage,
              stage_id: fullMoveStage.id,
              assignments: [myGroupPresence.id]
            },
          };
          setOperationRunning(true);
          return stageChangeInvestible(moveInfo)
            .then((newInv) => {
              onInvestibleStageChange(furtherWorkStage.id, newInv, id, marketId, commentsState,
                commentsDispatch, investiblesDispatch, () => {}, marketStagesState, undefined,
                getFullStage(marketStagesState, marketId, marketInfo.stage), marketPresencesDispatch);
              setOperationRunning(false);
            });
        }
      } else {
        navigate(history, `${formWizardLink(JOB_STAGE_WIZARD_TYPE, marketId, id)}&isAssign=${isAssigned}`);
      }
    } else {
      const inv = getInvestible(investibleState, id);
      const marketInfo = getMarketInfo(inv, marketId) || {};
      const { stage, group_id: currentGroupId } = marketInfo;
      // For now do nothing silently if dragging another view's job to an autonomous backlog
      if (currentGroupId === groupId) {
        if (stage !== furtherWorkStage.id) {
          // A job dragged from swimlanes to backlog is Not Ready, or it would be dragged to Next instead
          // A job in Next / Assist and dragged to backlog means the user doesn't want it in Next / Assist anymore
          // Silently resolve any comments that keep it in assist cause otherwise the job stays and is too confusing
          return changeInvestibleReady(id, stage, false, getAssistCommentIds(id));
        } else {
          const { open_for_investment: openForInvestment } = marketInfo;
          if (openForInvestment) {
            // Same thing here need to close anything that would keep it in assist
            return changeInvestibleReady(id, stage, false, getAssistCommentIds(id));
          } else {
            // We can't say for certain what is desired here so just go to wizard
            navigate(history, `${formWizardLink(JOB_STAGE_WIZARD_TYPE, marketId, id)}&stageId=${furtherWorkStage.id}`);
          }
        }
      }
    }
  }

  function onDropAssigned(event) {
    const id = event.dataTransfer.getData('text');
    const notificationType = event.dataTransfer.getData('notificationType');
    if (notificationType) {
      onDropBug(id, true);
    } else {
      onDropJob(id, true);
    }
  }

  function onDropBacklog(event) {
    const id = event.dataTransfer.getData('text');
    const notificationType = event.dataTransfer.getData('notificationType');
    if (notificationType) {
      onDropBug(id, false);
    } else {
      onDropJob(id, false);
    }
  }

  const tabTitle = `${groupName} ${intl.formatMessage({id: 'tabGroupAppend'})}`;
  const swimlaneEmptyPreText = 'There are no assigned jobs.';
  // Set inside getTabCount(2) so getTagLabel can tell whether the Bugs badge is the
  // "immediate" (red open bug) count or an unread-notification count (T-all-2237 / Q-all-175).
  let bugTabCountIsImmediate = false;
  // Same pattern for the autonomous Discussion badge: open question/suggestion count vs unread (B-all-508).
  let discussionTabCountIsOpen = false;
  const tabNewMessages = [[], [], [], []];
  function getTabCount(tabIndex) {
    if (!_.isEmpty(search)) {
      return undefined;
    }
    if (tabIndex === 0) {
      let investibleIds = (blockedOrRequiresInputOrReadyInvestiblesFullAssist || []).map((investible) =>
        investible.investible.id);
      investibleIds = investibleIds.concat((swimlaneInvestibles||[]).map((investible)=>investible.investible.id));
      // T-all-2422: Reviewable jobs display in this tab too, so count their notifications -
      // uncapped like the search list since the swimlane cap runs before the notification exemption
      investibleIds = investibleIds.concat((getInvestiblesInStage(investiblesFullAssist, inReviewStage.id,
        marketId) || []).map((investible) => investible.investible.id));
      const numNewMessagesRaw = findMessagesForInvestibleIds(investibleIds, syncedMessages, true)||[];
      const numNewMessages = numNewMessagesRaw.filter((message) => isInInbox(message));
      tabNewMessages[0] = numNewMessages;
      if (!_.isEmpty(numNewMessages)) {
        return `${_.size(numNewMessages)}`;
      }
    }
    if (tabIndex === 1) {
      let investibleIds = (furtherWorkReadyToStart || []).map((investible)=>investible.investible.id);
      investibleIds = investibleIds.concat((furtherWorkInvestibles||[]).map((investible)=>investible.investible.id));
      const numNewMessagesRaw = findMessagesForInvestibleIds(investibleIds, syncedMessages, true)||[];
      const numNewMessages = numNewMessagesRaw.filter((message) => isInInbox(message));
      tabNewMessages[1] = numNewMessages;
      if (!_.isEmpty(numNewMessages)) {
        return `${_.size(numNewMessages)}`;
      }
    }
    if (tabIndex === 2) {
      const todoRootIds = (todoGroupComments || []).map((comment) => comment.id);
      const todoRootIdSet = new Set(todoRootIds);
      // A reply is notified under its own comment id, and a reply can race ahead of its bug being resolved,
      // so fold in open-bug replies plus resolved bug roots and their replies (T-all-2237 / Q-all-174 / Q-all-175).
      const todoReplyIds = unResolvedGroupComments.filter((comment) => comment.comment_type === REPLY_TYPE &&
        todoRootIdSet.has(comment.root_comment_id)).map((comment) => comment.id);
      const commentIds = todoRootIds.concat(todoReplyIds)
        .concat((resolvedTodoGroupComments || []).map((comment) => comment.id));
      const numNewMessagesRaw = findMessagesForCommentIds(commentIds, syncedMessages, true);
      const numNewMessages = numNewMessagesRaw.filter((message) => isInInbox(message));
      tabNewMessages[2] = numNewMessages;
      if (!_.isEmpty(numNewMessages)) {
        bugTabCountIsImmediate = false;
        return `${_.size(numNewMessages)}`;
      }
      if (!_.isEmpty(criticalTodoGroupComments)) {
        bugTabCountIsImmediate = true;
        return `${_.size(criticalTodoGroupComments)}`;
      }
    }
    if (tabIndex === 3) {
      const discussionRootIds = (questionSuggestionGroupComments || []).map((comment) => comment.id);
      const discussionRootIdSet = new Set(discussionRootIds);
      // Fold in replies to questions/suggestions so their notifications count too (Q-all-174).
      const discussionReplyIds = unResolvedGroupComments.filter((comment) => comment.comment_type === REPLY_TYPE &&
        discussionRootIdSet.has(comment.root_comment_id)).map((comment) => comment.id);
      const commentIds = discussionRootIds.concat(discussionReplyIds);
      const numNewMessagesRaw = findMessagesForCommentIds(commentIds, syncedMessages, true);
      const numNewMessages = numNewMessagesRaw.filter((message) => isInInbox(message));
      tabNewMessages[3] = numNewMessages;
      if (!_.isEmpty(numNewMessages)) {
        discussionTabCountIsOpen = false;
        return `${_.size(numNewMessages)}`;
      }
      if (isAutonomous && !_.isEmpty(questionSuggestionGroupComments)) {
        discussionTabCountIsOpen = true;
        return `${_.size(questionSuggestionGroupComments)}`;
      }
    }
    return undefined;
  }
  function getTagLabel(tabCount, tabIndex) {
    if (tabCount) {
      if (!_.isEmpty(search)) {
        return intl.formatMessage({ id: 'match' });
      }
      if (tabIndex === 2) {
        return intl.formatMessage({ id: bugTabCountIsImmediate ? 'immediateLower' : 'new' });
      }
      if (isAutonomous && tabIndex === 3) {
        return intl.formatMessage({ id: discussionTabCountIsOpen ? 'openLower' : 'new' });
      }
      return intl.formatMessage({ id: 'new' });
    }
    return undefined;
  }
  const tabCount0 = getTabCount(0);
  const tabCount1 = getTabCount(1);
  const tabCount2 = getTabCount(2);
  const tabCount3 = getTabCount(3);
  const discussionOpenTarget = discussionTabCountIsOpen
    ? firstDisplayedOpenDiscussion(sortedGroupRoots) : undefined;
  function firstJobTarget(jobs) {
    return jobs[0] ? { url: formInvestibleLink(marketId, jobs[0].investible.id) } : undefined;
  }
  // Keep each badge's existing scope, but choose its target in the order its search list displays.
  const searchTargets = _.isEmpty(search) ? [] : [
    firstJobTarget(jobProgressSearchList),
    firstJobTarget(_.orderBy(furtherWorkReadyToStart.concat(furtherWorkInvestibles, notDoingInvestibles),
      [(inv) => inv.investible.created_at], ['desc'])),
    firstCommentMatch(_.orderBy(todoGroupComments,
      [(comment) => _.size(findMessagesForCommentIds([comment.id], messagesState.messages, true)),
        'updated_at'], ['desc', 'desc'])),
    firstCommentMatch(_.orderBy(questionSuggestionGroupComments, ['updated_at'], ['desc'])),
  ];

  function onDragOverNext(event) {
    event.dataTransfer.dropEffect = 'move';
    event.preventDefault();
  }

  function onDropNext(event) {
    const currentStageId = event.dataTransfer.getData('stageId');
    const fullStage = getFullStage(marketStagesState, marketId, currentStageId);
    if (isBlockedStage(fullStage) || isRequiredInputStage(fullStage)||isFurtherWorkStage(fullStage)) {
      // No op - already in this stage
      return;
    }
    const investibleId = event.dataTransfer.getData('text');
    if (!operationRunning) {
      const targetStageId = getFurtherWorkStage(marketStagesState, marketId)?.id;
      const moveInfo = {
        marketId,
        investibleId,
        stageInfo: {
          current_stage_id: currentStageId,
          stage_id: targetStageId,
          open_for_investment: true
        },
      };
      setOperationRunning(true);
      return stageChangeInvestible(moveInfo)
        .then((inv) => {
          onInvestibleStageChange(targetStageId, inv, investibleId, marketId, commentsState, commentsDispatch,
            investiblesDispatch, diffDispatch, marketStagesState, undefined, fullStage,
            marketPresencesDispatch);
        }).finally(() => {
          setOperationRunning(false);
        });
    }
  }

const isSwimlaneEmpty = _.isEmpty(swimlaneInvestibles)&&_.isEmpty(swimlaneCompleteInvestibles);
const isJobProgressEmpty = isSwimlaneEmpty && _.isEmpty(blockedOrRequiresInputOrReadyInvestiblesFullAssist);

  return (
    <Screen
      title={groupName}
      hidden={hidden}
      tabTitle={tabTitle}
      banner={banner}
      isKeptInMemory
      noPadDesktop
      showBanner={banner != null}
      openMenuItems={navListItemTextArray}
      navigationOptions={{useHoverFunctions: !mobileLayout, resetFunction: () => resetFunction(0)}}
    >
      <div style={{ paddingBottom: '0.25rem' }}>
        <GmailTabs
          value={tabIndex}
          addPaddingLeft='2rem'
          id='dialog-header'
          onChange={(event, value) => {
            resetFunction(value);
          }}
          indicatorColors={['#2F80ED', '#2F80ED', '#2F80ED', '#2F80ED']}>
          <GmailTabItem icon={iconOnlyTabs ? <AssignmentIndOutlined /> : undefined} onDrop={onDropAssigned}
                        tagLabel={getTagLabel(_.isEmpty(search) ? tabCount0 : jobsSearchResults)}
                        onTagClick={searchTargets[0] ? () => openMatch(searchTargets[0])
                          : (_.isEmpty(tabNewMessages[0]) ? undefined : () => openChipNotification(tabNewMessages[0]))}
                        tagTooltipId={searchTargets[0] ? 'searchGoToMatch' : undefined}
                        onDragOver={(event)=>event.preventDefault()} toolTipId='statusJobsToolTip' tagColor='#E85757'
                        label={intl.formatMessage({id: 'planningDialogNavStoriesLabel'})}
                        tag={_.isEmpty(search) || jobsSearchResults === 0 ? tabCount0 : `${jobsSearchResults}`} />
          <GmailTabItem icon={iconOnlyTabs ? <AssignmentOutlined /> : undefined} onDrop={onDropBacklog} tagLabel={getTagLabel(_.isEmpty(search) ? tabCount1 : backlogSearchResults)}
                        onTagClick={searchTargets[1] ? () => openMatch(searchTargets[1])
                          : (_.isEmpty(tabNewMessages[1]) ? undefined : () => openChipNotification(tabNewMessages[1]))}
                        tagTooltipId={searchTargets[1] ? 'searchGoToMatch' : undefined}
                        onDragOver={(event)=>event.preventDefault()} toolTipId='backlogJobsToolTip' tagColor='#E85757'
                        label={intl.formatMessage({id: 'planningDialogBacklog'})}
                        tag={_.isEmpty(search) || backlogSearchResults === 0 ? tabCount1 : `${backlogSearchResults}`} />
          <GmailTabItem icon={iconOnlyTabs ? <BugReportOutlined /> : undefined} label={intl.formatMessage({id: 'todoSection'})}
                        toolTipId='bugsToolTip' tagLabel={getTagLabel(_.isEmpty(search) ? tabCount2 : _.size(todoGroupComments), 2)} tagColor='#E85757'
                        onTagClick={searchTargets[2] ? () => openMatch(searchTargets[2])
                          : !_.isEmpty(tabNewMessages[2]) ? () => openChipNotification(tabNewMessages[2])
                          : (bugTabCountIsImmediate ? () => openCriticalBug(history,
                            firstDisplayedCriticalBug(criticalTodoGroupComments, messagesState.messages)) : undefined)}
                        tagTooltipId={searchTargets[2] ? 'searchGoToMatch'
                          : (bugTabCountIsImmediate && _.isEmpty(tabNewMessages[2]) ? 'notificationGoToBug' : undefined)}
                        tag={_.isEmpty(search) || _.isEmpty(todoGroupComments) ? tabCount2 : `${_.size(todoGroupComments)}` } />
          <GmailTabItem icon={iconOnlyTabs ? <LightbulbOutlined /> : undefined} toolTipId='discussionToolTip'
                        tagLabel={getTagLabel(_.isEmpty(search) ? tabCount3 : _.size(questionSuggestionGroupComments), 3)}
                        onTagClick={searchTargets[3] ? () => openMatch(searchTargets[3])
                          : !_.isEmpty(tabNewMessages[3]) ? () => openChipNotification(tabNewMessages[3])
                          : (discussionOpenTarget ? () => openCriticalBug(history, discussionOpenTarget) : undefined)}
                        tagTooltipId={searchTargets[3] ? 'searchGoToMatch'
                          : discussionOpenTarget && _.isEmpty(tabNewMessages[3])
                          ? (discussionOpenTarget.comment_type === QUESTION_TYPE
                            ? 'notificationGoToQuestion' : 'notificationGoToSuggestion')
                          : undefined}
                        label={intl.formatMessage({id: 'planningDialogDiscussionLabel'})} tagColor='#E85757'
                        tag={_.isEmpty(search) || _.isEmpty(questionSuggestionGroupComments) ? tabCount3 :
                          `${_.size(questionSuggestionGroupComments)}`} />
        </GmailTabs>
      </div>
      <div style={{display: 'flex', overflow: 'hidden'}}>
        <DialogOutset marketPresences={marketPresences} marketId={marketId} groupId={groupId} />
      <div style={{paddingTop: '0.5rem', width: '96%', marginLeft: 'auto', marginRight: 'auto', overflow: 'hidden'}}>
        <div ref={refToTop}></div>
        <InlineWizardLaunchContext.Provider value={{ openInlineWizard, inlineWizard }}>
        {/* J-all-325 (T-all-2186): an add wizard opened inside this view replaces the tab body, with the
           view tabs still above (Q-all-145, O-1). */}
        {inlineWizard && (
          <InlineWizardHost inlineWizard={inlineWizard} onClose={closeInlineWizard} />
        )}
        {!inlineWizard && (
        <>
        <DiscussionSection
          comments={notTodoGroupComments}
          resolvedComments={resolvedDiscussionComments}
          marketId={marketId}
          groupId={groupId}
          isSupport={isSupport}
          hidden={!isSectionOpen('discussionSection')}
        />
        {isSectionOpen('storiesSection') && (
          <div id="storiesSection" style={{overflowX: 'hidden'}}>
            {!_.isEmpty(search) ? (
              <div style={{ paddingTop: '0.5rem', paddingBottom: '5rem' }}>
                {!_.isEmpty(jobSearchData) && (
                  <div style={{ paddingBottom: '0.25rem' }}>
                    <div style={{ display: 'flex', width: '80%' }}>
                      <div style={{ flexGrow: 1 }} />
                      <Box fontSize={14} color="text.secondary">
                        {jobSearchFirst} - {jobSearchLast} of {_.size(jobProgressSearchList)}
                        <IconButton disabled={!jobSearchHasLess} onClick={() => setJobSearchPage(jobSearchPage - 1)}>
                          <KeyboardArrowLeft />
                        </IconButton>
                        <IconButton disabled={!jobSearchHasMore} onClick={() => setJobSearchPage(jobSearchPage + 1)}>
                          <KeyboardArrowRight />
                        </IconButton>
                      </Box>
                    </div>
                  </div>
                )}
                {jobSearchData.map((inv) => (
                  <BacklogItem
                    key={inv.investible.id}
                    inv={inv}
                    comments={marketComments}
                    marketPresences={marketPresences}
                    marketId={marketId}
                    myGroupPresence={myGroupPresence}
                    acceptedStageId={acceptedStage?.id}
                    inDialogStageId={inDialogStage?.id}
                    notDoingStageId={notDoingStage?.id}
                    furtherWorkStageId={furtherWorkStage?.id}
                    singleUser={isSingleUser ? humanGroupPresences[0] : undefined}
                    suppressNotifications
                  />
                ))}
              </div>
            ) : (
              <>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginTop: '1rem'}}>
                  <SpinningButton id="addJob"
                    className={mobileLayout ? wizardClasses.actionNextMobile : wizardClasses.actionNext}
                    icon={AddIcon} iconColor="black"
                    variant="text" doSpin={false}
                    style={{marginLeft: mobileLayout ? undefined : '0.5rem'}}
                    toolTipId='hotKeyJob'
                    onClick={() => openInlineWizard({ wizardType: JOB_WIZARD_TYPE, marketId, groupId })}>
                    <FormattedMessage id='addStoryLabel'/>
                  </SpinningButton>
                  {!_.isEmpty(archiveInvestiblesFullAssist) && (
                    <TooltipIconButton
                      id="nextArchiveCollapse"
                      translationId={nextArchiveCollapsed ? 'planningArchiveExpand' : 'planningArchiveCollapse'}
                      icon={nextArchiveCollapsed
                        ? <ExpandMoreIcon htmlColor={actionButtonColor} />
                        : <ExpandLess htmlColor={actionButtonColor} />}
                      onClick={() => updatePageState({ nextArchiveCollapsed: !nextArchiveCollapsed })}
                    />
                  )}
                </div>
                <div onDrop={onDropNext} onDragOver={onDragOverNext}
                  style={{paddingTop: !_.isEmpty(archiveInvestiblesFullAssist) && !nextArchiveCollapsed ? '0.5rem' : undefined}}>
                  <SubSection
                    type={NO_SECTION_TYPE}
                    bolder
                    id="blocked"
                    showCard={false}
                  >
                    {!_.isEmpty(archiveInvestiblesFullAssist) && !nextArchiveCollapsed && (
                      <ArchiveInvestbiles
                        comments={marketComments}
                        marketId={marketId}
                        presenceMap={presenceMap}
                        investibles={archiveInvestiblesFullAssist}
                        allowDragDrop
                        isAutonomous={isAutonomous}
                        isSingleUser={isSingleUser}
                        viewGroupId={groupId}
                      />
                    )}
                  </SubSection>
                </div>
                <div style={{ paddingBottom: isJobProgressEmpty ? undefined : '1rem' }}/>
                <DismissableText textId="notificationHelp" isLeft
                                 display={isJobProgressEmpty}
                                 text={
                                   isSingleUser ?
                                       <div>
                                         {swimlaneEmptyPreText} Use the "Add job" button above to start a new job.
                                       </div>
                                    : (isSupport ?
                                      <div>
                                        {swimlaneEmptyPreText} The "Add job" button above creates a job
                                        and sends a <Link href="https://documentation.uclusion.com/notifications"
                                                        target="_blank">notification</Link> to Uclusion support.
                                      </div>
                                      : (_.isEmpty(blockedOrRequiresInputOrReadyInvestiblesFullAssist) ?
                                     <div>
                                       {swimlaneEmptyPreText} The "Add job" button above creates a job
                                       and sends <Link href="https://documentation.uclusion.com/notifications"
                                                 target="_blank">notifications</Link> to members of this view.
                                     </div> : <div>
                                         {swimlaneEmptyPreText} Drag a job down to start or use the "Add job" button above.
                                       </div>)
                                  )
                  }/>
                <InvestiblesByPerson
                  comments={marketComments}
                  investibles={investiblesFullAssist}
                  visibleStages={visibleStages}
                  acceptedStage={acceptedStage}
                  inDialogStage={inDialogStage}
                  inBlockingStage={inBlockingStage}
                  inReviewStage={inReviewStage}
                  requiresInputStage={requiresInputStage}
                  group={group}
                  isAutonomous={isAutonomous}
                  mobileLayout={mobileLayout}
                  pageState={pageState} updatePageState={updatePageState}
                />
              </>
            )}
          </div>
        )}
        <div id="backlogSection" style={{overflowX: 'hidden'}}>
          <Backlog group={group} marketPresences={marketPresences} hidden={!isSectionOpen('backlogSection')} mobileLayout={mobileLayout}
                   furtherWorkReadyToStart={furtherWorkReadyToStart} furtherWorkInvestibles={furtherWorkInvestibles}
                   notDoingInvestibles={notDoingInvestibles} furtherWorkStageId={furtherWorkStage?.id}
                   comments={marketComments} myGroupPresence={myGroupPresence} inDialogStageId={inDialogStage?.id} notDoingStageId={notDoingStage?.id}
                   acceptedStageId={acceptedStage?.id} singleUser={isSingleUser ? humanGroupPresences[0] : undefined} />
        </div>
        <MarketTodos comments={unResolvedGroupComments} resolvedTodoComments={resolvedTodoGroupComments}
                     marketId={marketId} groupId={groupId} isSupport={isSupport}
                     sectionOpen={isSectionOpen('marketTodos')}
                     hidden={hidden} activeInvestibles={activeInvestibles}
                     setSectionOpen={() => {
                       updatePageState({sectionOpen: 'marketTodos', tabIndex: 2});
                     }} group={group} />
        </>
        )}
        </InlineWizardLaunchContext.Provider>
      </div>
      </div>
    </Screen>
  );
}

PlanningDialog.propTypes = {
  marketId: PropTypes.string.isRequired,
  marketInvestibles: PropTypes.arrayOf(PropTypes.object),
  marketPresences: PropTypes.arrayOf(PropTypes.object),
  marketStages: PropTypes.arrayOf(PropTypes.object),
  hidden: PropTypes.bool,
  comments: PropTypes.arrayOf(PropTypes.object),
  myPresence: PropTypes.object.isRequired,
};

export default PlanningDialog;
